import { InterventionSubTypes, InterventionTypes } from '@agroone/entities'

/** Columns expected in an intervention bulk-import CSV file. */
export const CSV_IMPORT_HEADERS = [
  'cropName',
  'note',
  'area',
  'areaUnit',
  'completedDate',
  'supplierId',
  'supplierIsFarm',
  'waterAmount',
  'waterUnit',
  'waterAreaUnit',
  'productName',
  'rate',
  'rateUnit',
  'rateAreaUnit',
] as const

export type CsvImportHeader = (typeof CSV_IMPORT_HEADERS)[number]
export type CsvImportRow = Record<CsvImportHeader, string>

const REQUIRED_ALWAYS: CsvImportHeader[] = ['cropName']

const REQUIRED_BY_SUBTYPE: Partial<Record<string, CsvImportHeader[]>> = {
  [InterventionSubTypes.PESTICIDES]: [
    'area',
    'areaUnit',
    'completedDate',
    'waterAmount',
    'waterUnit',
    'waterAreaUnit',
    'rate',
    'rateUnit',
    'rateAreaUnit',
  ],
}

/** Intervention type/subType combinations the CSV bulk-import currently supports. */
const SUPPORTED_SUBTYPES_BY_TYPE: Partial<Record<string, string[]>> = {
  [InterventionTypes.APPLICATIONS]: [InterventionSubTypes.PESTICIDES],
}

const CSV_COMPLETED_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/

/**
 * Business rules governing the CSV bulk-import of interventions: which columns are
 * required per intervention subType, which type/subType combos are supported, and
 * how a pesticide product is identified from a row (code vs. name).
 */
export const InterventionCsvImportRules = {
  headers: CSV_IMPORT_HEADERS,

  /** Columns required regardless of the intervention subType. */
  alwaysRequiredFields: REQUIRED_ALWAYS,

  /** Columns required only for the given subType (excludes the unconditional ones). */
  requiredFieldsForSubType: (subType: string): CsvImportHeader[] => REQUIRED_BY_SUBTYPE[subType] ?? [],

  /** Intervention types selectable for CSV bulk-import. */
  supportedTypes: (): string[] => Object.keys(SUPPORTED_SUBTYPES_BY_TYPE),

  /** SubTypes supported for CSV bulk-import under the given type. */
  supportedSubTypesFor: (type: string): string[] => SUPPORTED_SUBTYPES_BY_TYPE[type] ?? [],

  /** Whether the type/subType combination is supported for CSV bulk-import. */
  isBulkImportSupported: (type: string, subType: string): boolean =>
    (SUPPORTED_SUBTYPES_BY_TYPE[type] ?? []).includes(subType),

  /** Whether the column is required, either unconditionally or for the given subType. */
  isFieldRequired: (header: string, subType: string): boolean => {
    if ((REQUIRED_ALWAYS as string[]).includes(header)) return true
    return (REQUIRED_BY_SUBTYPE[subType] ?? []).includes(header as CsvImportHeader)
  },

  /** All required columns for the given subType (unconditional + subType-specific). */
  getRequiredFields: (subType: string): CsvImportHeader[] => [
    ...REQUIRED_ALWAYS,
    ...(REQUIRED_BY_SUBTYPE[subType] ?? []),
  ],

  /** CSV dates must be ISO 8601 UTC, e.g. 2026-04-15T00:00:00Z. */
  isValidCompletedDateFormat: (value: string): boolean => CSV_COMPLETED_DATE_RE.test(value),

  /** A pesticide row must identify its product by name. */
  hasPesticideIdentifier: (productName: string | null | undefined): boolean => Boolean(productName),
} as const
